import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { ACTION_TYPES, AUDIENCES, BLUE, blueSwitchSx, newButton, toButtonForm } from '../utils'

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    fontWeight={700}
    color="text.secondary"
    sx={{ letterSpacing: 1, display: 'block', mb: 1 }}
  >
    {children}
  </Typography>
)

SectionLabel.propTypes = { children: PropTypes.node.isRequired }

/**
 * Add or edit one suggestion chip. Nothing is sent to the API from here — the
 * row goes back to the page, which holds the whole list and saves it in one go.
 */
export default function ButtonFormModal({ button, onSubmit }) {
  const isEdit = Boolean(button)
  const [open, setOpen] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState(newButton())

  const handleOpen = () => {
    setForm(isEdit ? toButtonForm(button) : newButton())
    setError('')
    setOpen(true)
  }

  const handleClose = () => setOpen(false)

  const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }))
  const onText = (field) => (e) => setField(field)(e.target.value)

  const isLink = form.actionType === 'link'

  const validate = () => {
    if (!String(form.label).trim()) return 'Give the button a label'

    if (isLink && !String(form.link).trim()) {
      return 'Add the page this button should open'
    }

    if (!isLink && !String(form.prompt).trim()) {
      return 'Add the message this button should send to the bot'
    }

    return ''
  }

  const handleSubmit = () => {
    const message = validate()
    if (message) {
      setError(message)
      return
    }

    onSubmit({
      ...form,
      label: String(form.label).trim(),
      prompt: isLink ? '' : String(form.prompt).trim(),
      link: isLink ? String(form.link).trim() : '',
      newTab: isLink ? Boolean(form.newTab) : false,
    })

    setOpen(false)
  }

  return (
    <>
      {isEdit ? (
        <Tooltip title="Edit button">
          <IconButton size="small" onClick={handleOpen} sx={{ color: BLUE.main }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpen}
          sx={{
            backgroundColor: '#fff',
            color: BLUE.main,
            '&:hover': { backgroundColor: BLUE.soft },
          }}
        >
          Add Button
        </Button>
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          {isEdit ? 'Edit button' : 'New button'}
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider sx={{ m: 0 }} />

        <DialogContent>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <SectionLabel>WHAT IT SAYS</SectionLabel>

          <TextField
            fullWidth
            label="Button label"
            value={form.label}
            onChange={onText('label')}
            placeholder="Newest Gold Coast locations"
            helperText="Keep it short — chips sit side by side in a narrow window."
          />

          <Divider sx={{ my: 3 }} />

          <SectionLabel>WHO SEES IT</SectionLabel>

          <TextField
            select
            fullWidth
            label="Audience"
            value={form.audience}
            onChange={onText('audience')}
            helperText="Signed-in visitors see Everyone plus their own role. Logged-out visitors see every button, so they can pick their own way in."
          >
            {AUDIENCES.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>

          <Divider sx={{ my: 3 }} />

          <SectionLabel>WHAT IT DOES</SectionLabel>

          <TextField
            select
            fullWidth
            label="On click"
            value={form.actionType}
            onChange={onText('actionType')}
            helperText="Ask the bot for a normal chat reply, or send the visitor straight to a page."
          >
            {ACTION_TYPES.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>

          {isLink ? (
            <>
              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="Page to open"
                value={form.link}
                onChange={onText('link')}
                placeholder="/listings?city=Sydney"
                helperText="A path like /listings stays inside the site. A full https:// address goes off to another one."
              />

              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={Boolean(form.newTab)}
                    onChange={(e) => setField('newTab')(e.target.checked)}
                    sx={blueSwitchSx}
                  />
                }
                label="Open in a new tab"
              />
            </>
          ) : (
            <TextField
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              label="Message sent to the bot"
              value={form.prompt}
              onChange={onText('prompt')}
              placeholder="Show me newly listed spaces on the Gold Coast"
              helperText="Written as if the visitor typed it, so it can be longer and more specific than the label."
            />
          )}

          <Divider sx={{ my: 3 }} />

          <FormControlLabel
            control={
              <Switch
                checked={Boolean(form.isActive)}
                onChange={(e) => setField('isActive')(e.target.checked)}
                sx={blueSwitchSx}
              />
            }
            label="Show this button on the website"
          />
        </DialogContent>

        <Divider sx={{ m: 0 }} />

        <DialogActions>
          <Button onClick={handleClose} sx={{ color: BLUE.mutedText }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            // The dashboard theme paints contained-button text its own colour, and
            // repeats that in its hover rule — so white has to be stated twice.
            sx={{
              backgroundColor: BLUE.main,
              color: '#fff',
              '&:hover': { backgroundColor: BLUE.dark, color: '#fff' },
            }}
          >
            {isEdit ? 'Update button' : 'Add button'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

ButtonFormModal.defaultProps = { button: null }

ButtonFormModal.propTypes = {
  button: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
}
